import React, { useEffect, useState } from 'react';
import { IInvoiceFullObject } from '../../Interfaces/IInvoice';
import { IDocumentDownloaded } from '../../Interfaces/IDocumentDownloaded';
import { DocumentDownloadedDialog } from '../../Document/DocumentAssistant/DocumentDownloadedDialog';
import { DocumentPreviewDialog } from '../../Document/DocumentAssistant/DocumentPreviewDialog';
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Select,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { IDocument } from '../../Interfaces/IDocument';
import { useSidepanelDocumentContext } from '../LegalTransactionEdit';
import { getFetch } from '../../hooks/useFetch';
import {
  Delete,
  Download,
  FindInPage,
  Link,
  MoreVert,
  PictureInPicture,
  Search,
} from '@mui/icons-material';
import { IMIMEType } from '../../Interfaces/IMIMEType';
import { useDocumentTransferActions } from '../DocumentTransfer/DocumentTransferActions';
import { State } from '../../redux/mainReducer';
import { useSelector } from 'react-redux';
import { CustomeTextField } from '../../generic/CustomeTextField';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalTransactionType } from '../../Interfaces/ILegalTransactionType';
import { InvoiceStateChip } from '../../Invoice/InvoiceOverview';
import { DebtorAutocomplete } from '../../Invoice/DebtorAutocomplete';
import { IinvoiceState } from '../../Interfaces/IInvoiceState';
import { useResolvedDebtors } from '../../hooks/useResolvedDebtors';
import {
  mergeDebtorPool,
  resolveDebtors,
} from '../../services/debtorResolution.service';
import { ILegalRole } from '../../Interfaces/ILegalRole';
import { ILegalTransactionRecordNumber } from '../../Interfaces/ILegalTransactionRecordNumber';

interface IProps {
  invoiceObject: IInvoiceFullObject;
  invoiceObjectArray: IInvoiceFullObject[];
  setInvoiceObjectArray: Function;
  downloadedArray?: IDocumentDownloaded[];
  assignedLegalClients: ILegalClient[];
  setLegalClients: Function;
  legalTransactionTypes: ILegalTransactionType[];
  legalRoles: ILegalRole[];
  invoiceStates: IinvoiceState[] | undefined;
  isLoadinginvoiceStates: boolean;
  recordNumberArray: ILegalTransactionRecordNumber[];
}

const base64Download = (
  localDocumentObject: IDocument,
  mimeTypes: IMIMEType[]
) => {
  const currentMimeType = mimeTypes.find(
    x => x.idMIMEType === localDocumentObject.idMIMEType
  );
  if (localDocumentObject.Data && currentMimeType) {
    const byteCharacters = atob(localDocumentObject.Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: currentMimeType.MIMEType });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = localDocumentObject.FileName;
    a.click();
  }
};

export const InvoiceRow: React.FC<IProps> = ({
  invoiceObject,
  invoiceObjectArray,
  setInvoiceObjectArray,
  downloadedArray,
  assignedLegalClients,
  setLegalClients,
  legalTransactionTypes,
  invoiceStates,
  isLoadinginvoiceStates,
  recordNumberArray,
}) => {
  const scopes = localStorage.getItem('scope')?.split(',') ?? [];
  const { setSelectedSidepanelDocument } = useSidepanelDocumentContext();
  const mimeTypeArray = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );

  const [invoice, setInvoice] = useState(invoiceObject);
  const [localDocumentObject, setLocalDocumentObject] = useState<IDocument>({
    idDocument: invoice.idDocument,
    Data: invoice.Data,
    FileName: invoice.FileName,
    Created_at: new Date().toISOString(),
    idMIMEType: invoice.idMIMEType,
    idDocumentDirectory: null,
    idLegalTransaction: null,
    perEmail: null,
    isFromTemplate: null,
  });

  const [isOpenDownloaded, setIsOpenDownloaded] = useState(false);
  const [isOpenPreview, setisOpenPreview] = useState(false);

  // Ein Schuldner, der ueber „Alle Mandanten" im Rechnungs-Editor gewaehlt wurde,
  // gehoert nicht zum Vorgang und steht damit nicht in assignedLegalClients.
  // Ohne Nachladen bliebe sein Chip ohne Namen — und weil das Auswahlfeld beim
  // Aendern die sichtbaren Objekte zurueckschreibt, fiele er beim naechsten
  // Speichern still aus der Rechnung heraus (QS #8).
  const { extraDebtors } = useResolvedDebtors(
    invoice.LegalClients,
    assignedLegalClients
  );
  const debtorPool = mergeDebtorPool(assignedLegalClients, extraDebtors);

  // Aufgeloeste Schuldner-Objekte dieser Rechnung. Speist das Auswahlfeld; die
  // Warnleisten dieser Zeile sind mit #1283 in den Health Check gewandert, das
  // Nachladen bleibt aber noetig (QS #8).
  const selectedDebtors = resolveDebtors(invoice.LegalClients, debtorPool);

  useEffect(() => {
    setInvoiceObjectArray([
      ...invoiceObjectArray.map(x =>
        x.idInvoice === invoice.idInvoice ? invoice : x
      ),
    ]);
  }, [invoice]);

  /**
   * Aenderungen von aussen uebernehmen — z. B. ein Umbenennen der Datei, das
   * direkt ins `invoiceObjectArray` schreibt. Ohne diesen Abgleich wuerde die
   * Zeile ihren alten Stand behalten und ihn bei der naechsten eigenen Aenderung
   * zurueckschreiben.
   */
  useEffect(() => {
    if (invoiceObject.FileName !== invoice.FileName) {
      setInvoice(previous => ({
        ...previous,
        FileName: invoiceObject.FileName,
      }));
      setLocalDocumentObject(previous => ({
        ...previous,
        FileName: invoiceObject.FileName,
      }));
    }
  }, [invoiceObject.FileName, invoice.FileName]);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const transferActions = useDocumentTransferActions({
    documentObject: localDocumentObject,
    location: { kind: 'invoiceObject' },
    onOpenDialog: () => setMenuAnchor(null),
  });

  const handleOpenSidepanel = () => {
    setSelectedSidepanelDocument(localDocumentObject);

    if (!localDocumentObject.Data) {
      getFetch(
        '/document/',
        localDocumentObject.idDocument,
        (doc: IDocument) => {
          setLocalDocumentObject(doc);
          setSelectedSidepanelDocument(doc);
        }
      );
    }
  };

  const handleOpenPreview = () => {
    if (!localDocumentObject.Data) {
      getFetch(
        '/document/',
        localDocumentObject.idDocument,
        setLocalDocumentObject
      );
    }
    setisOpenPreview(true);
  };

  const handleRemove = () => {
    setInvoiceObjectArray([
      ...invoiceObjectArray.filter(x => x.idInvoice !== invoice.idInvoice),
    ]);
  };

  const handleDownload = async () => {
    if (localDocumentObject.Data) {
      base64Download(localDocumentObject, mimeTypeArray);
    } else {
      getFetch(
        '/document/',
        localDocumentObject.idDocument,
        (localDocumentObject: IDocument) => {
          setLocalDocumentObject(localDocumentObject);
          base64Download(localDocumentObject, mimeTypeArray);
        }
      );
    }
  };

  return (
    <>
      <DocumentDownloadedDialog
        open={isOpenDownloaded}
        onClose={() => setIsOpenDownloaded(false)}
        localDocumentObject={localDocumentObject}
        assignedLegalClients={assignedLegalClients}
      />
      <DocumentPreviewDialog
        open={isOpenPreview}
        localDocumentObject={localDocumentObject}
        onClose={() => setisOpenPreview(false)}
      />

      {/* #1283: Die Warnleisten aus #1183/#1185 (kein Schuldner, Schuldner
          ohne bzw. mit deaktivierter E-Mail) sind jetzt Health-Check-Regeln und
          erscheinen gebuendelt oberhalb des Reiters „Rechnungen", jeweils mit
          der betroffenen Rechnung im Text. `DebtorWarningBars` bleibt fuer die
          eigenstaendige Rechnungsmaske ausserhalb des Vorgangs. */}

      <TableRow>
        {/* Nr. */}
        <TableCell sx={{ width: 72 }}>
          {invoice.idInvoice < 0 ? 'Neu' : invoice.idInvoice}
        </TableCell>

        {/* Runter. */}
        {downloadedArray && (
          <TableCell sx={{ width: 72 }}>
            {
              downloadedArray.filter(
                d => d.idDocument === localDocumentObject.idDocument
              ).length
            }
          </TableCell>
        )}

        {/* Art + Aktenzeichen untereinander */}
        <TableCell sx={{ width: 260 }}>
          <CustomeTextField
            label='Art'
            attr='idLegalTransactionType'
            object={invoice}
            setObject={setInvoice}
            type='select'
            maxWidth={240}
          >
            {legalTransactionTypes.map(type => (
              <MenuItem
                key={`type-${type.idLegalTransactionType}`}
                value={type.idLegalTransactionType}
              >
                {type.LegalTransactionType}
              </MenuItem>
            ))}
          </CustomeTextField>

          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth size='small'>
              <InputLabel id='recordnumber-label' shrink>
                Urkundennummer
              </InputLabel>
              <Select
                labelId='recordnumber-label'
                label='Urkundennummer'
                displayEmpty
                notched
                value={invoice.idLegalTransactionRecordNumber ?? ''}
                onChange={e => {
                  const value = e.target.value as number;
                  setInvoice({
                    ...invoice,
                    idLegalTransactionRecordNumber: value,
                  });
                }}
              >
                {recordNumberArray
                  // Leere Einträge ausblenden (z.B. der Default-Init-Eintrag),
                  // aber neu hinzugefügte mit Inhalt + negativer tempId zulassen,
                  // damit Rechnungen direkt darauf referenziert werden können.
                  .filter(rn => (rn.RecordNumberEntry ?? '').trim() !== '')
                  .map(rn => {
                    const isNew = (rn.idLegalTransactionRecordNumber ?? 0) < 0;
                    return (
                      <MenuItem
                        key={`rn-${rn.idLegalTransactionRecordNumber}`}
                        value={rn.idLegalTransactionRecordNumber}
                      >
                        {rn.RecordNumberEntry}
                        {isNew ? ' (neu, ungespeichert)' : ''}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          </Box>
        </TableCell>

        {/* Status */}
        <TableCell sx={{ width: 120 }}>
          {isLoadinginvoiceStates ? (
            <CircularProgress size={20} />
          ) : invoiceStates === undefined ? (
            <>Fehler</>
          ) : (
            <InvoiceStateChip
              idInvoiceState={invoice.idInvoiceState}
              invoiceStates={invoiceStates}
            />
          )}
        </TableCell>

        {/* Schuldner + Summe */}
        <TableCell sx={{ width: 420 }}>
          <DebtorAutocomplete
            options={assignedLegalClients}
            value={selectedDebtors}
            onChange={selected => {
              const ids = selected.map(c => c.idLegalClient);
              setInvoice({ ...invoice, LegalClients: ids });
              setLegalClients([
                ...assignedLegalClients.map(client => ({
                  ...client,
                  enableInvoiceReminding: ids.includes(client.idLegalClient),
                })),
              ]);
            }}
          />
          <Box sx={{ mt: 1, maxWidth: 180 }}>
            <CustomeTextField
              attr='Total'
              label='Summe'
              object={invoice}
              setObject={setInvoice}
              type='euro'
              maxWidth={180}
            />
          </Box>
        </TableCell>

        {/* Dateiname */}
        <TableCell sx={{ width: 240, maxWidth: 240 }}>
          <Tooltip title={localDocumentObject.FileName}>
            <Typography
              variant='body2'
              noWrap
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {localDocumentObject.FileName}
            </Typography>
          </Tooltip>
        </TableCell>

        {/* Aktionen — sichtbar bleiben Bild-in-Bild und Vorschau, der Rest
            steckt im Drei-Punkte-Menue (wie in der internen Datenablage). */}
        <TableCell
          sx={{ width: 140 }}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
        >
          {/* Dialoge ausserhalb des Menues — sonst schliessen sie sich sofort. */}
          {transferActions.dialogs}
          <Tooltip title='Bild-in-Bild öffnen'>
            <IconButton size='small' onClick={handleOpenSidepanel}>
              <PictureInPicture fontSize='small' />
            </IconButton>
          </Tooltip>

          <Tooltip title='Vorschau'>
            <IconButton size='small' onClick={handleOpenPreview}>
              <FindInPage fontSize='small' />
            </IconButton>
          </Tooltip>

          <Tooltip title='Weitere Aktionen'>
            <IconButton
              size='small'
              onClick={(event: React.MouseEvent<HTMLElement>) =>
                setMenuAnchor(event.currentTarget)
              }
            >
              <MoreVert fontSize='small' />
            </IconButton>
          </Tooltip>
          <Menu
            open={menuAnchor !== null}
            anchorEl={menuAnchor}
            onClose={() => setMenuAnchor(null)}
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
          >
            <MenuList dense>
              <MenuItem
                onClick={() => {
                  handleDownload();
                  setMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <Download fontSize='small' />
                </ListItemIcon>
                <ListItemText>Herunterladen</ListItemText>
              </MenuItem>
              {localDocumentObject.idDocument > 0 && (
                <MenuItem
                  onClick={() => {
                    setIsOpenDownloaded(true);
                    setMenuAnchor(null);
                  }}
                >
                  <ListItemIcon>
                    <Search fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>Anzahl Downloads</ListItemText>
                </MenuItem>
              )}
              <Divider />
              {transferActions.menuItems}
              {!!(scopes.includes('bill') || scopes.includes('admin')) && (
                <MenuItem
                  onClick={() => {
                    window.open(`/invoices/${invoice.idInvoice}`, '_blank');
                    setMenuAnchor(null);
                  }}
                >
                  <ListItemIcon>
                    <Link fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>Zur Rechnung</ListItemText>
                </MenuItem>
              )}
              <Divider />
              <MenuItem
                onClick={() => {
                  handleRemove();
                  setMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <Delete fontSize='small' />
                </ListItemIcon>
                <ListItemText>Löschen</ListItemText>
              </MenuItem>
            </MenuList>
          </Menu>
        </TableCell>
      </TableRow>
    </>
  );
};
